import { z } from 'zod';
import {
  ModalSessionFlightPolicySchema,
  type ModalSessionFlightPolicy,
  type ModalSessionFlightPolicyRule,
} from '@voll-intelligence/domain';

import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { ChatUiFlightCabinSchema } from '../../shared/schema/flight_cabin_schema.js';
import { ChatUiFlightSegmentSchema } from '../../shared/schema/flight_segment_schema.js';
import { CHAT_UI_MAX_OFFERS } from '../../shared/schema/limits.js';
import { chatUiBase } from '../../shared/schema/base.js';

const ChatUiFlightBadgeSchema = z.object({
  label: z.string().min(1),
  kind: z.enum(['best', 'cheap', 'fast', 'flex', 'direct', 'alt']),
});
export type ChatUiFlightBadge = z.infer<typeof ChatUiFlightBadgeSchema>;

// Veredito de conformidade da tarifa com a política corporativa, por oferta
// (produtor agents, determinístico — derivado do `searchPolicies` do
// flight-business). `status:'out'` quando ALGUMA regra reprova — é o agregado que
// guia o selo e a exigência de justificativa. `rules` lista TODAS as regras
// avaliadas (antecedência/classe/bagagem) com status próprio e, quando o
// `flightPolicyDescriptives` está disponível, o `text` descritivo rico — alimenta
// o drawer "Detalhes da Política". Ausente → oferta sem dado de política (ex.:
// stub) degrada sem selo.
export type ChatUiFlightPolicyRule = ModalSessionFlightPolicyRule;
const ChatUiFlightPolicySchema = ModalSessionFlightPolicySchema;
export type ChatUiFlightPolicy = ModalSessionFlightPolicy;

/** Perna de VOLTA de um combo ida+volta (round-trip em card único). Os campos
 *  `segments`/`stops` existentes da oferta seguem sendo a IDA. Opcional →
 *  payload antigo (por perna) continua válido; client antigo (objeto não-strict)
 *  descarta o campo e renderiza só a ida. */
const ChatUiFlightOfferLegSchema = z.object({
  segments: z.array(ChatUiFlightSegmentSchema).min(1),
  stops: z.number().int().min(0).optional(),
});
export type ChatUiFlightOfferLeg = z.infer<typeof ChatUiFlightOfferLegSchema>;

// Exportado porque o `flight_multi_offers` reusa a MESMA oferta por trecho — o
// trio do multi é o mesmo objeto do card binário, e duplicar o shape criaria
// duas verdades sobre o que é uma oferta de voo.
export const ChatUiFlightOfferSchema = z.object({
  offerId: z.string().min(1),
  segments: z.array(ChatUiFlightSegmentSchema).min(1),
  price: ChatUiMoneySchema,
  fareClass: z.string(),
  // Paradas da oferta, fonte autoritativa do provider (o item de busca do
  // flight-business traz `stops`; o http provider colapsa a oferta num único
  // segmento, então a contagem NÃO é derivável de `segments.length`). Ausente
  // (ex.: stub) → derivado de `segments.length - 1`.
  stops: z.number().int().min(0).optional(),
  // Enriquecimento determinístico do carrossel (produtor agents): superlativos
  // ranqueados sobre o conjunto + racional templatado. Opcionais → degrada para
  // card sem badges/legenda. NUNCA prosa do LLM.
  badges: z.array(ChatUiFlightBadgeSchema).max(3).optional(),
  why: z.string().optional(),
  // Conformidade com a política corporativa (opcional → degrada sem selo).
  policy: ChatUiFlightPolicySchema.optional(),
  // Perna de volta do combo (round-trip card único). Ausente = one-way ou
  // payload legado por perna.
  returnLeg: ChatUiFlightOfferLegSchema.optional(),
});
export type ChatUiFlightOffer = z.infer<typeof ChatUiFlightOfferSchema>;

/** Cap da lista completa que alimenta a drawer de filtro (web-chat). Maior que
 *  o carrossel curado, mas ainda limita o tamanho do frame SSE em buscas largas.
 *  100 sai de medição (out/2026): mediana de 51 ofertas por busca e 97% das
 *  buscas até 100; ~2 KB por oferta dá ~200 KB por card, longe do 1 MiB do SNS.
 *  A cena do agente numera no MESMO teto (`logic/scene_cut.ts`): subir aqui
 *  aumenta também o retrato que o modelo lê. */
export const CHAT_UI_MAX_ALL_OFFERS = 100;

/**
 * Eco da busca que produziu o card — espelha o corpo do `POST /flight/search`
 * do BFF (`FlightSearchBodySchema`, app-integration): rota IATA + datas ISO
 * (`returnDate` presente ⇒ round-trip) + cabine + sujeito da política. É a
 * SEMENTE do FlightExplorer (re-busca server-side do drawer): sem ele o drawer
 * degrada pro filtro client-side legado. Determinístico (projeção dos slots do
 * agents), nunca prosa do LLM.
 */
export const ChatUiFlightSearchEchoSchema = z.object({
  origin: z.string().regex(/^[A-Z]{3}$/),
  destination: z.string().regex(/^[A-Z]{3}$/),
  departureDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  returnDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  cabin: ChatUiFlightCabinSchema.optional(),
  policyTravelerId: z.string().min(1).optional(),
});
export type ChatUiFlightSearchEcho = z.infer<typeof ChatUiFlightSearchEchoSchema>;

/**
 * A união de tarifas de UMA oferta binária, em DOIS lugares: no `chosen` do bloco
 * (eco do servidor) e na selection do CTA. Os shapes são spreads e não
 * `.extend()`: os dois membros da selection precisam continuar `.strict()` com a
 * MESMA ordem de união de hoje (split primeiro), e spread de shape não depende de
 * como o Zod propaga `unknownKeys` numa extensão.
 *
 * ESPELHA o `ExplorerFares` (tipo TS de `shared/explorer/explorer_state.ts`), que
 * permanece tipo — a faixa do explorer não valida em runtime. Um teste de
 * atribuição mútua trava os dois juntos.
 */
const splitFaresShape = {
  outboundFareId: z.string().min(1),
  returnFareId: z.string().min(1).optional(),
};
const bundledFaresShape = { bundledFareId: z.string().min(1) };

export const ChatUiFlightFaresSchema = z.union([
  z.object(splitFaresShape).strict(),
  z.object(bundledFaresShape).strict(),
]);
export type ChatUiFlightFares = z.infer<typeof ChatUiFlightFaresSchema>;

/** Membro `flight_offers` do `ChatUiSchema` (discriminatedUnion). */
export const FlightOffersBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('flight_offers'),
    offers: z.array(ChatUiFlightOfferSchema).min(1).max(CHAT_UI_MAX_OFFERS),
    // Lista completa (sem badges) que alimenta a drawer de filtro do web-chat.
    // Opcional: ausente ⇒ sem CTA/drawer (degrade); `offers` segue curado.
    allOffers: z.array(ChatUiFlightOfferSchema).max(CHAT_UI_MAX_ALL_OFFERS).optional(),
    // Perna do ida-e-volta (rótulo do header "Melhores voos · Ida/Volta").
    // `outbound` = IDA de um round-trip; `return` = VOLTA. Ausente = só ida (one-way).
    leg: z.enum(['outbound', 'return']).optional(),
    // Eco da busca que produziu o card — semente do explorer server-backed do
    // drawer. Opcional: ausente ⇒ drawer degrada pro filtro client-side legado
    // (payload antigo continua válido).
    search: ChatUiFlightSearchEchoSchema.optional(),
    // Presente ⇒ card interativo novo estilo: linha expande tarifas inline
    // (port flightTariff) e o pick sai como `interaction` (selections
    // `select_offer_fares`). Ausente ⇒ payload legado: display-only.
    challengeId: z.string().min(1).optional(),
    // Expande a primeira oferta ao montar (card focado pós select_flight por texto).
    autoExpand: z.boolean().optional(),
    // Eco do servidor: a oferta e as tarifas que FECHARAM esta viagem. Presente
    // ⇒ o card é recibo, não escolha — e o congelamento é INTRÍNSECO (policy
    // `frozenWhen` da entry), porque `resolvedInteractionIds` é best-effort e
    // persiste DEPOIS de o evento do agente ser publicado: pendurar o freeze só
    // nele pode renderizar um frame clicável. NÃO é o `{offerId, fareId, price}`
    // do multi: um fareId único só descreve one-way, e um round-trip split
    // escolhe DUAS tarifas.
    chosen: z
      .object({
        offerId: z.string().min(1),
        fares: ChatUiFlightFaresSchema,
        price: ChatUiMoneySchema,
      })
      .strict()
      .optional(),
  })
  .strict();

/**
 * Payload ecoado em `confirmation.selections` pelo CTA "Selecionar esta tarifa"
 * — discriminado por `action: 'select_offer_fares'` no submit_guard do flight
 * (mesmo padrão do `FlightPlannerSelectionSchema`; payloads sem `action` caem no
 * switch por fase). Duas variantes estritas: split (outbound + return opcional)
 * e bundled (comboId `ida|volta`). NUNCA entra no schema de decisões do LLM.
 */
export const FlightOfferFareSelectionSchema = z.union([
  z
    .object({
      action: z.literal('select_offer_fares'),
      offerId: z.string().min(1),
      ...splitFaresShape,
    })
    .strict(),
  z
    .object({
      action: z.literal('select_offer_fares'),
      offerId: z.string().min(1),
      ...bundledFaresShape,
    })
    .strict(),
]);
export type FlightOfferFareSelection = z.infer<typeof FlightOfferFareSelectionSchema>;

/**
 * Selection do "Buscar de novo" da faixa de opção expirada (#5277), despachada
 * como `interaction` com o `challengeId` do card — `flight_offers` ou
 * `flight_multi_offers`. Só o gesto: quem sabe QUAL busca refazer é o estado do
 * agente (os critérios em vigor), não o card. Estrita, com `action` literal que
 * não colide com nenhuma outra selection. NUNCA entra no schema de decisões do LLM.
 */
export const FlightResearchOffersSelectionSchema = z
  .object({ action: z.literal('research_offers') })
  .strict();
export type FlightResearchOffersSelection = z.infer<typeof FlightResearchOffersSelectionSchema>;
